// Landing-page translations for all 11 languages, merged into the i18n
// "translation" namespace under the `landing` key (see i18n/index.js).
/* eslint-disable */

const en = {
  nav: { features: "Features", how: "How it works", pricing: "Pricing", faq: "FAQ" },
  header: { launchApp: "Launch App", buy: "Buy" },
  hero: {
    eyebrow: "Pro Wheel Alignment · In Your Pocket",
    title1: "Shop-grade alignment,", title2: "no alignment rack.",
    subtitle: "Thortek Performance turns your phone into a precision inclinometer and AI vision rig. Measure camber, caster and toe, decode any VIN, and dial in 150+ factory specs — right in the driveway.",
    getLifetime: "Get Lifetime Access", tryApp: "Try the App",
    oneTime: "One-time payment", noSub: "No subscription", buyAnywhere: "Buy from anywhere",
  },
  statLabels: ["Factory-spec vehicles", "Sensor resolution", "Languages", "Visual scan engine"],
  features: {
    label: "Everything in one app", heading: "The whole alignment bay, reimagined for your phone.",
    cards: [
      { title: "Quick Align Telemetry", desc: "Live four-wheel dashboard with g-sensor wheels that tilt in real time against OEM targets." },
      { title: "Hands-Free Voice Capture", desc: "Just say “capture” to lock a reading — no tapping, no reaching for the phone while you're under the vehicle." },
      { title: "Tilt Gauge", desc: "Digital inclinometer for camber plus a guided caster steering-sweep — accurate to a fraction of a degree." },
      { title: "AI Visual Scan", desc: "Point your camera, capture a smooth panoramic axle sweep, and let AI read camber and toe for you." },
      { title: "Multi-Vehicle Garage", desc: "150+ factory specs through 2026, VIN decoding, lift/leveling presets and custom vehicle profiles." },
      { title: "Printable Session Logs", desc: "Before/after comparisons exported to a clean, print-ready PDF report for clients or records." },
      { title: "11 Languages + RTL", desc: "Fully localized including Arabic right-to-left — ready for any garage, anywhere." },
    ],
  },
  showcase: {
    label: "See it in action", heading: "Real screens from the Thortek app.",
    items: [
      { tag: "Quick Align", title: "Live four-wheel telemetry", desc: "Watch each corner's camber and toe update in real time, graded pass/fail against factory spec with tires that lean as you move the phone." },
      { tag: "Tilt Gauge", title: "A precision digital inclinometer", desc: "A tactical radial gauge turns your phone's motion sensors into a shop-grade camber and caster readout — accurate to a fraction of a degree." },
      { tag: "Visual Scan", title: "AI reads the angle for you", desc: "Point the camera, sweep the axle, and the AI vision engine detects camber and toe from the image with a live confidence meter." },
      { tag: "Garage", title: "Every spec, decoded and ready", desc: "Decode any VIN, pick from 150+ factory profiles, apply lift and leveling presets, and keep custom vehicles — all in one place." },
    ],
  },
  how: {
    label: "How it works", heading: "From driveway to in-spec in three steps.", inSpec: "In spec",
    steps: [
      { title: "Pick your vehicle", desc: "Decode a VIN or select from 150+ factory profiles. Add lift or leveling presets instantly." },
      { title: "Measure", desc: "Use the tilt gauge, g-sensor dashboard or AI camera scan to capture camber, caster and toe." },
      { title: "Dial in & log", desc: "Compare against OEM spec, pass/fail each wheel, then export a print-ready session report." },
    ],
  },
  compare: {
    label: "Why Thortek", heading: "Thortek vs. typical alignment tools.",
    intro: "Most tools are either a pricey shop rack or a one-trick phone app. Thortek is the whole bay — hardware-free, AI-powered, and paid for once.",
    thortek: "Thortek", typical: "Typical tools",
    included: "Included", partial: "Sometimes / extra cost", notAvail: "Not available",
    cta: "Get lifetime access",
    rows: [
      { f: "One-time price — no subscription", note: "Pay once, yours forever" },
      { f: "Works with just your phone — no hardware" },
      { f: "AI camera reads camber & toe" },
      { f: "AR live angle overlay" },
      { f: "AI tire-wear diagnosis" },
      { f: "Corner-balance calculator" },
      { f: "150+ factory specs + VIN decode" },
      { f: "Shareable before/after reports" },
      { f: "11 languages + RTL support" },
      { f: "Installs on any phone or tablet" },
      { f: "No login — private, local data" },
    ],
  },
  pricing: {
    label: "Simple pricing", heading: "One payment. Yours forever.",
    subtitle: "No subscriptions, no per-vehicle fees. Pay once and own the full suite.",
    cardTitle: "Lifetime License", cardSub: "Full suite · All features", bestValue: "Best value",
    unit: "USD · one-time", buyNow: "Buy now",
    secure: "Secure Stripe checkout", worldwide: "Cards worldwide",
    intlNote: "International customers welcome — pay in your local card currency from Brazil and 190+ countries.",
    includes: [
      "Quick Align live telemetry dashboard",
      "Tilt Gauge: camber + guided caster sweep",
      "AI Visual Scan with panoramic axle sweep",
      "Multi-vehicle garage + VIN decoding",
      "Lift & leveling presets, custom vehicles",
      "Printable PDF session logs",
      "All 11 languages, lifetime updates",
    ],
  },
  testimonials: {
    label: "Trusted in the pits", heading: "Built for enthusiasts and pros alike.",
    quotes: [
      { q: "I dialed in my track camber in the paddock without hauling it to a shop. The caster sweep is shockingly accurate.", r: "Time-attack driver" },
      { q: "The AI scan reads toe faster than my string setup. Pays for itself the first weekend.", r: "Independent mechanic" },
      { q: "150 factory specs and VIN decode in one place — my whole crew uses it on every corner-balance job.", r: "Suspension shop owner" },
    ],
  },
  faq: {
    label: "Questions", heading: "Everything you need to know.",
    items: [
      { q: "Is this really a one-time purchase?", a: "Yes. Pay {{price}} once and the full Thortek Performance suite is yours — no monthly fees, no per-vehicle charges, and future updates included." },
      { q: "Can I buy from outside the United States?", a: "Absolutely. Checkout is powered by Stripe and accepts cards from Brazil and 190+ countries. Your bank converts to your local currency automatically." },
      { q: "Do I need special hardware?", a: "No rack or sensors required. Thortek uses your phone's motion sensors and camera. A flat, level surface gives the most accurate readings." },
      { q: "How do I access the app after buying?", a: "The moment your payment clears you'll receive a license key and can launch the full app right in your browser — no download needed." },
      { q: "Which vehicles are supported?", a: "150+ factory-spec vehicles through 2026 plus VIN decoding and custom vehicle profiles, so you can align almost anything." },
      { q: "Is my data private?", a: "Your alignment sessions are stored locally on your device. No account or login is required to use the tool." },
    ],
  },
  finalCta: { headingA: "Ready to get", headingB: "dialed in?", subtitle: "Own the full suite today for a single {{price}} payment.", buy: "Buy Lifetime Access", launch: "Launch the App" },
  footer: { features: "Features", pricing: "Pricing", disclaimer: "Disclaimer", terms: "Terms", privacy: "Privacy", refunds: "Refunds", contact: "Contact", launchApp: "Launch App" },
};

const es = {
  nav: { features: "Funciones", how: "Cómo funciona", pricing: "Precio", faq: "Preguntas" },
  header: { launchApp: "Abrir app", buy: "Comprar" },
  hero: {
    eyebrow: "Alineación profesional · En tu bolsillo",
    title1: "Alineación de taller,", title2: "sin alineadora.",
    subtitle: "Thortek Performance convierte tu teléfono en un inclinómetro de precisión y un sistema de visión con IA. Mide caída, avance y convergencia, decodifica cualquier VIN y ajusta más de 150 especificaciones de fábrica — en tu propia cochera.",
    getLifetime: "Acceso de por vida", tryApp: "Probar la app",
    oneTime: "Pago único", noSub: "Sin suscripción", buyAnywhere: "Compra desde cualquier lugar",
  },
  statLabels: ["Vehículos con especificación de fábrica", "Resolución del sensor", "Idiomas", "Motor de escaneo visual"],
  features: {
    label: "Todo en una app", heading: "Toda la bahía de alineación, reinventada para tu teléfono.",
    cards: [
      { title: "Telemetría Quick Align", desc: "Panel en vivo de cuatro ruedas con sensores g que se inclinan en tiempo real frente a los objetivos OEM." },
      { title: "Captura por voz manos libres", desc: "Solo di «capturar» para fijar una lectura — sin tocar ni alcanzar el teléfono mientras estás debajo del vehículo." },
      { title: "Medidor de inclinación", desc: "Inclinómetro digital para la caída más un barrido guiado de avance — con precisión de una fracción de grado." },
      { title: "Escaneo visual con IA", desc: "Apunta la cámara, captura un barrido panorámico del eje y deja que la IA lea la caída y la convergencia por ti." },
      { title: "Garaje multivehículo", desc: "Más de 150 especificaciones de fábrica hasta 2026, decodificación de VIN, ajustes de elevación y perfiles personalizados." },
      { title: "Registros imprimibles", desc: "Comparaciones de antes/después exportadas a un informe PDF limpio y listo para imprimir para clientes o archivos." },
      { title: "11 idiomas + RTL", desc: "Totalmente localizado, incluido el árabe de derecha a izquierda — listo para cualquier taller, en cualquier lugar." },
    ],
  },
  showcase: {
    label: "Míralo en acción", heading: "Pantallas reales de la app Thortek.",
    items: [
      { tag: "Quick Align", title: "Telemetría en vivo de cuatro ruedas", desc: "Observa la caída y la convergencia de cada esquina actualizarse en tiempo real, calificadas frente a la especificación de fábrica con ruedas que se inclinan al mover el teléfono." },
      { tag: "Medidor de inclinación", title: "Un inclinómetro digital de precisión", desc: "Un medidor radial convierte los sensores de tu teléfono en una lectura de caída y avance de nivel profesional — con precisión de una fracción de grado." },
      { tag: "Escaneo visual", title: "La IA lee el ángulo por ti", desc: "Apunta la cámara, barre el eje y el motor de visión con IA detecta la caída y la convergencia desde la imagen con un medidor de confianza en vivo." },
      { tag: "Garaje", title: "Cada especificación, decodificada y lista", desc: "Decodifica cualquier VIN, elige entre más de 150 perfiles de fábrica, aplica ajustes de elevación y guarda vehículos personalizados — todo en un solo lugar." },
    ],
  },
  how: {
    label: "Cómo funciona", heading: "De la cochera a dentro de especificación en tres pasos.", inSpec: "En especificación",
    steps: [
      { title: "Elige tu vehículo", desc: "Decodifica un VIN o elige entre más de 150 perfiles de fábrica. Añade ajustes de elevación al instante." },
      { title: "Mide", desc: "Usa el medidor de inclinación, el panel de sensores g o el escaneo con IA para capturar caída, avance y convergencia." },
      { title: "Ajusta y registra", desc: "Compara con la especificación OEM, aprueba/reprueba cada rueda y exporta un informe listo para imprimir." },
    ],
  },
  compare: {
    label: "Por qué Thortek", heading: "Thortek frente a las herramientas típicas.",
    intro: "La mayoría de las herramientas son una alineadora cara o una app de un solo truco. Thortek es toda la bahía — sin hardware, con IA y de pago único.",
    thortek: "Thortek", typical: "Herramientas típicas",
    included: "Incluido", partial: "A veces / coste extra", notAvail: "No disponible",
    cta: "Acceso de por vida",
    rows: [
      { f: "Pago único — sin suscripción", note: "Paga una vez, tuyo para siempre" },
      { f: "Funciona solo con tu teléfono — sin hardware" },
      { f: "La cámara con IA lee caída y convergencia" },
      { f: "Superposición de ángulo en vivo (AR)" },
      { f: "Diagnóstico de desgaste de neumáticos con IA" },
      { f: "Calculadora de balance por esquina" },
      { f: "Más de 150 especificaciones + decodificación VIN" },
      { f: "Informes de antes/después compartibles" },
      { f: "11 idiomas + soporte RTL" },
      { f: "Se instala en cualquier teléfono o tablet" },
      { f: "Sin inicio de sesión — datos privados y locales" },
    ],
  },
  pricing: {
    label: "Precio sencillo", heading: "Un pago. Tuyo para siempre.",
    subtitle: "Sin suscripciones ni tarifas por vehículo. Paga una vez y posee la suite completa.",
    cardTitle: "Licencia de por vida", cardSub: "Suite completa · Todas las funciones", bestValue: "Mejor valor",
    unit: "USD · pago único", buyNow: "Comprar ahora",
    secure: "Pago seguro con Stripe", worldwide: "Tarjetas de todo el mundo",
    intlNote: "Clientes internacionales bienvenidos — paga en la moneda de tu tarjeta desde Brasil y más de 190 países.",
    includes: [
      "Panel de telemetría en vivo Quick Align",
      "Medidor de inclinación: caída + barrido de avance guiado",
      "Escaneo visual con IA y barrido panorámico del eje",
      "Garaje multivehículo + decodificación de VIN",
      "Ajustes de elevación y vehículos personalizados",
      "Registros de sesión en PDF imprimibles",
      "Los 11 idiomas, actualizaciones de por vida",
    ],
  },
  testimonials: {
    label: "De confianza en los boxes", heading: "Hecho para aficionados y profesionales por igual.",
    quotes: [
      { q: "Ajusté la caída de pista en el paddock sin llevarla al taller. El barrido de avance es increíblemente preciso.", r: "Piloto de time-attack" },
      { q: "El escaneo con IA lee la convergencia más rápido que mi montaje con cuerdas. Se paga solo el primer fin de semana.", r: "Mecánico independiente" },
      { q: "150 especificaciones de fábrica y decodificación VIN en un solo lugar — todo mi equipo lo usa en cada balance por esquina.", r: "Dueño de taller de suspensión" },
    ],
  },
  faq: {
    label: "Preguntas", heading: "Todo lo que necesitas saber.",
    items: [
      { q: "¿Es realmente una compra única?", a: "Sí. Paga {{price}} una vez y toda la suite Thortek Performance es tuya — sin tarifas mensuales, sin cargos por vehículo y con actualizaciones futuras incluidas." },
      { q: "¿Puedo comprar desde fuera de Estados Unidos?", a: "Por supuesto. El pago usa Stripe y acepta tarjetas de Brasil y más de 190 países. Tu banco convierte a tu moneda local automáticamente." },
      { q: "¿Necesito hardware especial?", a: "No se requiere alineadora ni sensores. Thortek usa los sensores de movimiento y la cámara de tu teléfono. Una superficie plana y nivelada da las lecturas más precisas." },
      { q: "¿Cómo accedo a la app tras comprar?", a: "En cuanto se confirme el pago recibirás una clave de licencia y podrás abrir la app completa en tu navegador — sin descargas." },
      { q: "¿Qué vehículos son compatibles?", a: "Más de 150 vehículos con especificación de fábrica hasta 2026, además de decodificación de VIN y perfiles personalizados, para alinear casi cualquier cosa." },
      { q: "¿Mis datos son privados?", a: "Tus sesiones de alineación se guardan localmente en tu dispositivo. No se requiere cuenta ni inicio de sesión." },
    ],
  },
  finalCta: { headingA: "¿Listo para", headingB: "ajustarlo?", subtitle: "Obtén la suite completa hoy por un único pago de {{price}}.", buy: "Acceso de por vida", launch: "Abrir la app" },
  footer: { features: "Funciones", pricing: "Precio", disclaimer: "Aviso legal", terms: "Términos", privacy: "Privacidad", refunds: "Reembolsos", contact: "Contacto", launchApp: "Abrir app" },
};

const fr = {
  nav: { features: "Fonctions", how: "Comment ça marche", pricing: "Tarif", faq: "FAQ" },
  header: { launchApp: "Ouvrir l'app", buy: "Acheter" },
  hero: {
    eyebrow: "Géométrie pro · Dans votre poche",
    title1: "Géométrie de garage,", title2: "sans banc d'alignement.",
    subtitle: "Thortek Performance transforme votre téléphone en inclinomètre de précision et en système de vision IA. Mesurez carrossage, chasse et parallélisme, décodez n'importe quel VIN et réglez plus de 150 specs d'usine — directement dans votre allée.",
    getLifetime: "Accès à vie", tryApp: "Essayer l'app",
    oneTime: "Paiement unique", noSub: "Sans abonnement", buyAnywhere: "Achetez de partout",
  },
  statLabels: ["Véhicules aux specs d'usine", "Résolution du capteur", "Langues", "Moteur de scan visuel"],
  features: {
    label: "Tout dans une app", heading: "Toute la baie d'alignement, repensée pour votre téléphone.",
    cards: [
      { title: "Télémétrie Quick Align", desc: "Tableau de bord quatre roues en direct avec capteurs g qui s'inclinent en temps réel face aux cibles OEM." },
      { title: "Capture vocale mains libres", desc: "Dites simplement « capture » pour verrouiller une mesure — sans toucher ni attraper le téléphone sous le véhicule." },
      { title: "Inclinomètre", desc: "Inclinomètre numérique pour le carrossage plus un balayage de chasse guidé — précis au dixième de degré." },
      { title: "Scan visuel IA", desc: "Pointez la caméra, capturez un balayage panoramique de l'essieu et laissez l'IA lire le carrossage et le parallélisme." },
      { title: "Garage multi-véhicules", desc: "Plus de 150 specs d'usine jusqu'en 2026, décodage VIN, préréglages de rehausse et profils personnalisés." },
      { title: "Rapports imprimables", desc: "Comparaisons avant/après exportées en PDF propre et prêt à imprimer pour vos clients ou vos archives." },
      { title: "11 langues + RTL", desc: "Entièrement localisé, y compris l'arabe de droite à gauche — prêt pour n'importe quel atelier, partout." },
    ],
  },
  showcase: {
    label: "Voyez-le en action", heading: "De vrais écrans de l'app Thortek.",
    items: [
      { tag: "Quick Align", title: "Télémétrie quatre roues en direct", desc: "Voyez le carrossage et le parallélisme de chaque coin se mettre à jour en temps réel, notés face aux specs d'usine, avec des roues qui s'inclinent quand vous bougez le téléphone." },
      { tag: "Inclinomètre", title: "Un inclinomètre numérique de précision", desc: "Une jauge radiale transforme les capteurs de mouvement de votre téléphone en une lecture de carrossage et de chasse de niveau pro — au dixième de degré." },
      { tag: "Scan visuel", title: "L'IA lit l'angle pour vous", desc: "Pointez la caméra, balayez l'essieu, et le moteur de vision IA détecte le carrossage et le parallélisme depuis l'image avec un indicateur de confiance en direct." },
      { tag: "Garage", title: "Chaque spec, décodée et prête", desc: "Décodez n'importe quel VIN, choisissez parmi plus de 150 profils d'usine, appliquez des préréglages de rehausse et gardez des véhicules personnalisés — au même endroit." },
    ],
  },
  how: {
    label: "Comment ça marche", heading: "De l'allée aux specs en trois étapes.", inSpec: "Dans les specs",
    steps: [
      { title: "Choisissez votre véhicule", desc: "Décodez un VIN ou choisissez parmi plus de 150 profils d'usine. Ajoutez des préréglages de rehausse instantanément." },
      { title: "Mesurez", desc: "Utilisez l'inclinomètre, le tableau de bord à capteurs g ou le scan IA pour capturer carrossage, chasse et parallélisme." },
      { title: "Réglez et enregistrez", desc: "Comparez aux specs OEM, validez chaque roue, puis exportez un rapport prêt à imprimer." },
    ],
  },
  compare: {
    label: "Pourquoi Thortek", heading: "Thortek face aux outils classiques.",
    intro: "La plupart des outils sont soit un banc coûteux, soit une app à usage unique. Thortek, c'est toute la baie — sans matériel, propulsé par l'IA, et payé une seule fois.",
    thortek: "Thortek", typical: "Outils classiques",
    included: "Inclus", partial: "Parfois / coût supplémentaire", notAvail: "Non disponible",
    cta: "Accès à vie",
    rows: [
      { f: "Paiement unique — sans abonnement", note: "Payez une fois, à vous pour toujours" },
      { f: "Fonctionne avec votre seul téléphone — sans matériel" },
      { f: "La caméra IA lit carrossage et parallélisme" },
      { f: "Superposition d'angle en direct (AR)" },
      { f: "Diagnostic d'usure des pneus par IA" },
      { f: "Calculateur d'équilibrage par coin" },
      { f: "Plus de 150 specs d'usine + décodage VIN" },
      { f: "Rapports avant/après partageables" },
      { f: "11 langues + prise en charge RTL" },
      { f: "S'installe sur tout téléphone ou tablette" },
      { f: "Sans connexion — données privées et locales" },
    ],
  },
  pricing: {
    label: "Tarif simple", heading: "Un paiement. À vous pour toujours.",
    subtitle: "Sans abonnement ni frais par véhicule. Payez une fois et possédez la suite complète.",
    cardTitle: "Licence à vie", cardSub: "Suite complète · Toutes les fonctions", bestValue: "Meilleur rapport",
    unit: "USD · unique", buyNow: "Acheter",
    secure: "Paiement sécurisé Stripe", worldwide: "Cartes du monde entier",
    intlNote: "Clients internationaux bienvenus — payez dans la devise de votre carte depuis le Brésil et plus de 190 pays.",
    includes: [
      "Tableau de bord de télémétrie Quick Align",
      "Inclinomètre : carrossage + balayage de chasse guidé",
      "Scan visuel IA avec balayage panoramique de l'essieu",
      "Garage multi-véhicules + décodage VIN",
      "Préréglages de rehausse et véhicules personnalisés",
      "Rapports de session PDF imprimables",
      "Les 11 langues, mises à jour à vie",
    ],
  },
  testimonials: {
    label: "Approuvé dans les stands", heading: "Conçu pour les passionnés comme pour les pros.",
    quotes: [
      { q: "J'ai réglé mon carrossage de piste au paddock sans aller au garage. Le balayage de chasse est bluffant de précision.", r: "Pilote time-attack" },
      { q: "Le scan IA lit le parallélisme plus vite que mon montage à ficelle. Rentabilisé dès le premier week-end.", r: "Mécanicien indépendant" },
      { q: "150 specs d'usine et décodage VIN au même endroit — toute mon équipe l'utilise sur chaque équilibrage.", r: "Patron d'atelier de suspension" },
    ],
  },
  faq: {
    label: "Questions", heading: "Tout ce qu'il faut savoir.",
    items: [
      { q: "Est-ce vraiment un achat unique ?", a: "Oui. Payez {{price}} une fois et toute la suite Thortek Performance est à vous — sans frais mensuels, sans frais par véhicule, mises à jour incluses." },
      { q: "Puis-je acheter hors des États-Unis ?", a: "Absolument. Le paiement passe par Stripe et accepte les cartes du Brésil et de plus de 190 pays. Votre banque convertit automatiquement dans votre devise." },
      { q: "Ai-je besoin de matériel spécial ?", a: "Aucun banc ni capteur requis. Thortek utilise les capteurs de mouvement et la caméra de votre téléphone. Une surface plane et de niveau donne les mesures les plus précises." },
      { q: "Comment accéder à l'app après l'achat ?", a: "Dès le paiement validé, vous recevez une clé de licence et pouvez lancer l'app complète dans votre navigateur — sans téléchargement." },
      { q: "Quels véhicules sont pris en charge ?", a: "Plus de 150 véhicules aux specs d'usine jusqu'en 2026, plus le décodage VIN et des profils personnalisés, pour aligner presque tout." },
      { q: "Mes données sont-elles privées ?", a: "Vos sessions d'alignement sont stockées localement sur votre appareil. Aucun compte ni connexion requis." },
    ],
  },
  finalCta: { headingA: "Prêt à", headingB: "tout régler ?", subtitle: "Obtenez la suite complète aujourd'hui pour un unique paiement de {{price}}.", buy: "Accès à vie", launch: "Ouvrir l'app" },
  footer: { features: "Fonctions", pricing: "Tarif", disclaimer: "Avertissement", terms: "Conditions", privacy: "Confidentialité", refunds: "Remboursements", contact: "Contact", launchApp: "Ouvrir l'app" },
};

const de = {
  nav: { features: "Funktionen", how: "So funktioniert's", pricing: "Preis", faq: "FAQ" },
  header: { launchApp: "App öffnen", buy: "Kaufen" },
  hero: {
    eyebrow: "Profi-Achsvermessung · In deiner Tasche",
    title1: "Werkstatt-Vermessung,", title2: "ohne Achsmessstand.",
    subtitle: "Thortek Performance macht dein Handy zum Präzisions-Neigungsmesser und KI-Vision-System. Miss Sturz, Nachlauf und Spur, decodiere jede VIN und stelle über 150 Werksspezifikationen ein — direkt in der Einfahrt.",
    getLifetime: "Lebenslanger Zugang", tryApp: "App testen",
    oneTime: "Einmalzahlung", noSub: "Kein Abo", buyAnywhere: "Kauf von überall",
  },
  statLabels: ["Fahrzeuge mit Werksspezifikation", "Sensorauflösung", "Sprachen", "Visuelle Scan-Engine"],
  features: {
    label: "Alles in einer App", heading: "Der ganze Vermessungsstand, neu gedacht fürs Handy.",
    cards: [
      { title: "Quick-Align-Telemetrie", desc: "Live-Dashboard für vier Räder mit g-Sensoren, die sich in Echtzeit gegen OEM-Vorgaben neigen." },
      { title: "Freihändige Sprachaufnahme", desc: "Sag einfach „Capture“, um einen Messwert zu fixieren — kein Tippen, kein Griff zum Handy unter dem Fahrzeug." },
      { title: "Neigungsmesser", desc: "Digitaler Neigungsmesser für Sturz plus geführter Nachlauf-Lenkeinschlag — auf den Bruchteil eines Grads genau." },
      { title: "KI-Visualscan", desc: "Kamera draufhalten, einen sauberen Panorama-Achsschwenk aufnehmen und die KI liest Sturz und Spur für dich." },
      { title: "Multi-Fahrzeug-Garage", desc: "Über 150 Werksspezifikationen bis 2026, VIN-Decodierung, Höher-/Tieferlegungs-Presets und eigene Profile." },
      { title: "Druckbare Protokolle", desc: "Vorher/Nachher-Vergleiche als sauberer, druckfertiger PDF-Bericht für Kunden oder Unterlagen." },
      { title: "11 Sprachen + RTL", desc: "Vollständig lokalisiert inklusive Arabisch von rechts nach links — bereit für jede Werkstatt, überall." },
    ],
  },
  showcase: {
    label: "In Aktion sehen", heading: "Echte Screens aus der Thortek-App.",
    items: [
      { tag: "Quick Align", title: "Live-Telemetrie für vier Räder", desc: "Sieh Sturz und Spur jeder Ecke in Echtzeit, bewertet gegen Werksspezifikation, mit Reifen, die sich neigen, wenn du das Handy bewegst." },
      { tag: "Neigungsmesser", title: "Ein präziser digitaler Neigungsmesser", desc: "Eine Radialanzeige macht die Bewegungssensoren deines Handys zur werkstattreifen Sturz- und Nachlauf-Anzeige — auf den Bruchteil eines Grads genau." },
      { tag: "Visualscan", title: "Die KI liest den Winkel für dich", desc: "Kamera draufhalten, Achse schwenken, und die KI-Vision-Engine erkennt Sturz und Spur aus dem Bild mit Live-Vertrauensanzeige." },
      { tag: "Garage", title: "Jede Spezifikation, decodiert und bereit", desc: "Decodiere jede VIN, wähle aus über 150 Werksprofilen, wende Presets an und speichere eigene Fahrzeuge — alles an einem Ort." },
    ],
  },
  how: {
    label: "So funktioniert's", heading: "Von der Einfahrt zur Vorgabe in drei Schritten.", inSpec: "Im Soll",
    steps: [
      { title: "Fahrzeug wählen", desc: "VIN decodieren oder aus über 150 Werksprofilen wählen. Presets sofort hinzufügen." },
      { title: "Messen", desc: "Nutze Neigungsmesser, g-Sensor-Dashboard oder KI-Kamerascan für Sturz, Nachlauf und Spur." },
      { title: "Einstellen & protokollieren", desc: "Mit OEM-Vorgabe vergleichen, jedes Rad bewerten und einen druckfertigen Bericht exportieren." },
    ],
  },
  compare: {
    label: "Warum Thortek", heading: "Thortek vs. typische Werkzeuge.",
    intro: "Die meisten Werkzeuge sind entweder ein teurer Messstand oder eine Ein-Trick-App. Thortek ist der ganze Stand — ohne Hardware, mit KI und einmal bezahlt.",
    thortek: "Thortek", typical: "Typische Tools",
    included: "Enthalten", partial: "Teils / Aufpreis", notAvail: "Nicht verfügbar",
    cta: "Lebenslanger Zugang",
    rows: [
      { f: "Einmalzahlung — kein Abo", note: "Einmal zahlen, für immer deins" },
      { f: "Funktioniert nur mit deinem Handy — keine Hardware" },
      { f: "KI-Kamera liest Sturz & Spur" },
      { f: "AR-Live-Winkelüberlagerung" },
      { f: "KI-Reifenverschleiß-Diagnose" },
      { f: "Eckgewichts-Rechner" },
      { f: "Über 150 Werksspezifikationen + VIN-Decodierung" },
      { f: "Teilbare Vorher/Nachher-Berichte" },
      { f: "11 Sprachen + RTL-Unterstützung" },
      { f: "Installiert auf jedem Handy oder Tablet" },
      { f: "Kein Login — private, lokale Daten" },
    ],
  },
  pricing: {
    label: "Einfacher Preis", heading: "Eine Zahlung. Für immer deins.",
    subtitle: "Keine Abos, keine Gebühren pro Fahrzeug. Einmal zahlen und die komplette Suite besitzen.",
    cardTitle: "Lebenslange Lizenz", cardSub: "Komplette Suite · Alle Funktionen", bestValue: "Bestes Angebot",
    unit: "USD · einmalig", buyNow: "Jetzt kaufen",
    secure: "Sicherer Stripe-Checkout", worldwide: "Karten weltweit",
    intlNote: "Internationale Kunden willkommen — zahle in deiner Kartenwährung aus Brasilien und über 190 Ländern.",
    includes: [
      "Quick-Align-Live-Telemetrie-Dashboard",
      "Neigungsmesser: Sturz + geführter Nachlauf-Schwenk",
      "KI-Visualscan mit Panorama-Achsschwenk",
      "Multi-Fahrzeug-Garage + VIN-Decodierung",
      "Höher-/Tieferlegungs-Presets, eigene Fahrzeuge",
      "Druckbare PDF-Sitzungsprotokolle",
      "Alle 11 Sprachen, lebenslange Updates",
    ],
  },
  testimonials: {
    label: "Bewährt in der Box", heading: "Gebaut für Enthusiasten und Profis.",
    quotes: [
      { q: "Ich habe meinen Rennsturz im Fahrerlager eingestellt, ohne in die Werkstatt zu fahren. Der Nachlauf-Schwenk ist erstaunlich genau.", r: "Time-Attack-Fahrer" },
      { q: "Der KI-Scan liest die Spur schneller als mein Schnuraufbau. Zahlt sich am ersten Wochenende aus.", r: "Freier Mechaniker" },
      { q: "150 Werksspezifikationen und VIN-Decodierung an einem Ort — mein ganzes Team nutzt es bei jedem Eckgewichts-Job.", r: "Inhaber Fahrwerkswerkstatt" },
    ],
  },
  faq: {
    label: "Fragen", heading: "Alles, was du wissen musst.",
    items: [
      { q: "Ist das wirklich ein einmaliger Kauf?", a: "Ja. Zahle {{price}} einmal und die komplette Thortek-Performance-Suite gehört dir — keine Monatsgebühren, keine Gebühren pro Fahrzeug, künftige Updates inklusive." },
      { q: "Kann ich außerhalb der USA kaufen?", a: "Absolut. Der Checkout läuft über Stripe und akzeptiert Karten aus Brasilien und über 190 Ländern. Deine Bank rechnet automatisch in deine Währung um." },
      { q: "Brauche ich spezielle Hardware?", a: "Kein Messstand, keine Sensoren nötig. Thortek nutzt die Bewegungssensoren und Kamera deines Handys. Eine ebene Fläche liefert die genauesten Messwerte." },
      { q: "Wie greife ich nach dem Kauf auf die App zu?", a: "Sobald deine Zahlung bestätigt ist, erhältst du einen Lizenzschlüssel und kannst die volle App direkt im Browser starten — kein Download nötig." },
      { q: "Welche Fahrzeuge werden unterstützt?", a: "Über 150 Fahrzeuge mit Werksspezifikation bis 2026 plus VIN-Decodierung und eigene Profile, sodass du fast alles vermessen kannst." },
      { q: "Sind meine Daten privat?", a: "Deine Vermessungssitzungen werden lokal auf deinem Gerät gespeichert. Kein Konto und kein Login erforderlich." },
    ],
  },
  finalCta: { headingA: "Bereit zum", headingB: "Einstellen?", subtitle: "Hol dir die komplette Suite heute für eine einmalige Zahlung von {{price}}.", buy: "Lebenslangen Zugang kaufen", launch: "App öffnen" },
  footer: { features: "Funktionen", pricing: "Preis", disclaimer: "Haftungsausschluss", terms: "AGB", privacy: "Datenschutz", refunds: "Erstattungen", contact: "Kontakt", launchApp: "App öffnen" },
};

const pt = {
  nav: { features: "Recursos", how: "Como funciona", pricing: "Preço", faq: "Perguntas" },
  header: { launchApp: "Abrir app", buy: "Comprar" },
  hero: {
    eyebrow: "Alinhamento profissional · No seu bolso",
    title1: "Alinhamento de oficina,", title2: "sem alinhador.",
    subtitle: "O Thortek Performance transforma seu celular em um inclinômetro de precisão e um sistema de visão com IA. Meça câmber, cáster e convergência, decodifique qualquer chassi (VIN) e ajuste mais de 150 especificações de fábrica — na sua própria garagem.",
    getLifetime: "Acesso vitalício", tryApp: "Testar o app",
    oneTime: "Pagamento único", noSub: "Sem assinatura", buyAnywhere: "Compre de qualquer lugar",
  },
  statLabels: ["Veículos com especificação de fábrica", "Resolução do sensor", "Idiomas", "Motor de escaneamento visual"],
  features: {
    label: "Tudo em um app", heading: "Toda a baia de alinhamento, reinventada para o seu celular.",
    cards: [
      { title: "Telemetria Quick Align", desc: "Painel ao vivo de quatro rodas com sensores g que inclinam em tempo real frente aos alvos OEM." },
      { title: "Captura por voz sem usar as mãos", desc: "Basta dizer “capturar” para fixar uma leitura — sem tocar nem alcançar o celular enquanto está embaixo do veículo." },
      { title: "Medidor de inclinação", desc: "Inclinômetro digital para câmber mais uma varredura guiada de cáster — preciso a uma fração de grau." },
      { title: "Escaneamento visual com IA", desc: "Aponte a câmera, capture uma varredura panorâmica do eixo e deixe a IA ler câmber e convergência por você." },
      { title: "Garagem multiveículos", desc: "Mais de 150 especificações de fábrica até 2026, decodificação de VIN, predefinições de elevação e perfis personalizados." },
      { title: "Registros imprimíveis", desc: "Comparações antes/depois exportadas em um relatório PDF limpo e pronto para imprimir para clientes ou arquivos." },
      { title: "11 idiomas + RTL", desc: "Totalmente localizado, incluindo árabe da direita para a esquerda — pronto para qualquer oficina, em qualquer lugar." },
    ],
  },
  showcase: {
    label: "Veja em ação", heading: "Telas reais do app Thortek.",
    items: [
      { tag: "Quick Align", title: "Telemetria ao vivo de quatro rodas", desc: "Veja o câmber e a convergência de cada canto atualizarem em tempo real, avaliados frente à especificação de fábrica, com pneus que inclinam ao mover o celular." },
      { tag: "Medidor de inclinação", title: "Um inclinômetro digital de precisão", desc: "Um medidor radial transforma os sensores de movimento do seu celular numa leitura de câmber e cáster de nível profissional — a uma fração de grau." },
      { tag: "Escaneamento visual", title: "A IA lê o ângulo por você", desc: "Aponte a câmera, varra o eixo, e o motor de visão com IA detecta câmber e convergência a partir da imagem com um medidor de confiança ao vivo." },
      { tag: "Garagem", title: "Cada especificação, decodificada e pronta", desc: "Decodifique qualquer VIN, escolha entre mais de 150 perfis de fábrica, aplique predefinições e mantenha veículos personalizados — tudo num só lugar." },
    ],
  },
  how: {
    label: "Como funciona", heading: "Da garagem à especificação em três passos.", inSpec: "Na especificação",
    steps: [
      { title: "Escolha seu veículo", desc: "Decodifique um VIN ou escolha entre mais de 150 perfis de fábrica. Adicione predefinições de elevação na hora." },
      { title: "Meça", desc: "Use o medidor de inclinação, o painel de sensores g ou o escaneamento com IA para capturar câmber, cáster e convergência." },
      { title: "Ajuste e registre", desc: "Compare com a especificação OEM, aprove/reprove cada roda e exporte um relatório pronto para imprimir." },
    ],
  },
  compare: {
    label: "Por que Thortek", heading: "Thortek vs. ferramentas comuns.",
    intro: "A maioria das ferramentas é um alinhador caro ou um app de um truque só. O Thortek é a baia inteira — sem hardware, com IA e pago uma única vez.",
    thortek: "Thortek", typical: "Ferramentas comuns",
    included: "Incluído", partial: "Às vezes / custo extra", notAvail: "Indisponível",
    cta: "Acesso vitalício",
    rows: [
      { f: "Pagamento único — sem assinatura", note: "Pague uma vez, seu para sempre" },
      { f: "Funciona só com seu celular — sem hardware" },
      { f: "Câmera com IA lê câmber e convergência" },
      { f: "Sobreposição de ângulo ao vivo (AR)" },
      { f: "Diagnóstico de desgaste de pneus com IA" },
      { f: "Calculadora de balanceamento por canto" },
      { f: "Mais de 150 especificações + decodificação VIN" },
      { f: "Relatórios antes/depois compartilháveis" },
      { f: "11 idiomas + suporte RTL" },
      { f: "Instala em qualquer celular ou tablet" },
      { f: "Sem login — dados privados e locais" },
    ],
  },
  pricing: {
    label: "Preço simples", heading: "Um pagamento. Seu para sempre.",
    subtitle: "Sem assinaturas, sem taxas por veículo. Pague uma vez e tenha a suíte completa.",
    cardTitle: "Licença vitalícia", cardSub: "Suíte completa · Todos os recursos", bestValue: "Melhor valor",
    unit: "USD · único", buyNow: "Comprar agora",
    secure: "Checkout seguro Stripe", worldwide: "Cartões do mundo todo",
    intlNote: "Clientes internacionais são bem-vindos — pague na moeda do seu cartão, do Brasil e de mais de 190 países.",
    includes: [
      "Painel de telemetria ao vivo Quick Align",
      "Medidor de inclinação: câmber + varredura de cáster guiada",
      "Escaneamento visual com IA e varredura panorâmica do eixo",
      "Garagem multiveículos + decodificação de VIN",
      "Predefinições de elevação e veículos personalizados",
      "Registros de sessão em PDF imprimíveis",
      "Todos os 11 idiomas, atualizações vitalícias",
    ],
  },
  testimonials: {
    label: "Confiável nos boxes", heading: "Feito para entusiastas e profissionais.",
    quotes: [
      { q: "Ajustei meu câmber de pista no paddock sem levar à oficina. A varredura de cáster é incrivelmente precisa.", r: "Piloto de time-attack" },
      { q: "O escaneamento com IA lê a convergência mais rápido que meu barbante. Se paga no primeiro fim de semana.", r: "Mecânico independente" },
      { q: "150 especificações de fábrica e decodificação de VIN num só lugar — toda a minha equipe usa em cada balanceamento.", r: "Dono de oficina de suspensão" },
    ],
  },
  faq: {
    label: "Perguntas", heading: "Tudo o que você precisa saber.",
    items: [
      { q: "É mesmo uma compra única?", a: "Sim. Pague {{price}} uma vez e toda a suíte Thortek Performance é sua — sem mensalidades, sem taxas por veículo e com atualizações futuras incluídas." },
      { q: "Posso comprar de fora dos Estados Unidos?", a: "Com certeza. O checkout usa Stripe e aceita cartões do Brasil e de mais de 190 países. Seu banco converte para a sua moeda automaticamente." },
      { q: "Preciso de hardware especial?", a: "Nenhum alinhador ou sensor é necessário. O Thortek usa os sensores de movimento e a câmera do seu celular. Uma superfície plana e nivelada dá as leituras mais precisas." },
      { q: "Como acesso o app após comprar?", a: "Assim que o pagamento for aprovado, você recebe uma chave de licença e pode abrir o app completo no navegador — sem download." },
      { q: "Quais veículos são compatíveis?", a: "Mais de 150 veículos com especificação de fábrica até 2026, além de decodificação de VIN e perfis personalizados, para alinhar quase tudo." },
      { q: "Meus dados são privados?", a: "Suas sessões de alinhamento são salvas localmente no seu dispositivo. Nenhuma conta ou login é necessário." },
    ],
  },
  finalCta: { headingA: "Pronto para", headingB: "acertar tudo?", subtitle: "Tenha a suíte completa hoje por um único pagamento de {{price}}.", buy: "Acesso vitalício", launch: "Abrir o app" },
  footer: { features: "Recursos", pricing: "Preço", disclaimer: "Aviso legal", terms: "Termos", privacy: "Privacidade", refunds: "Reembolsos", contact: "Contato", launchApp: "Abrir app" },
};

const it = {
  nav: { features: "Funzioni", how: "Come funziona", pricing: "Prezzo", faq: "FAQ" },
  header: { launchApp: "Apri app", buy: "Acquista" },
  hero: {
    eyebrow: "Assetto professionale · In tasca",
    title1: "Assetto da officina,", title2: "senza ponte di allineamento.",
    subtitle: "Thortek Performance trasforma il telefono in un inclinometro di precisione e un sistema di visione con IA. Misura campanatura, incidenza e convergenza, decodifica qualsiasi VIN e imposta oltre 150 specifiche di fabbrica — direttamente in cortile.",
    getLifetime: "Accesso a vita", tryApp: "Prova l'app",
    oneTime: "Pagamento unico", noSub: "Nessun abbonamento", buyAnywhere: "Acquista ovunque",
  },
  statLabels: ["Veicoli con specifica di fabbrica", "Risoluzione del sensore", "Lingue", "Motore di scansione visiva"],
  features: {
    label: "Tutto in un'app", heading: "L'intero reparto assetto, ripensato per il telefono.",
    cards: [
      { title: "Telemetria Quick Align", desc: "Cruscotto live a quattro ruote con sensori g che si inclinano in tempo reale rispetto ai valori OEM." },
      { title: "Acquisizione vocale a mani libere", desc: "Basta dire “capture” per bloccare una lettura — senza toccare né raggiungere il telefono mentre sei sotto il veicolo." },
      { title: "Inclinometro", desc: "Inclinometro digitale per la campanatura più una sterzata guidata per l'incidenza — preciso a una frazione di grado." },
      { title: "Scansione visiva con IA", desc: "Punta la fotocamera, cattura una panoramica fluida dell'asse e lascia che l'IA legga campanatura e convergenza per te." },
      { title: "Garage multi-veicolo", desc: "Oltre 150 specifiche di fabbrica fino al 2026, decodifica VIN, preset di sollevamento e profili personalizzati." },
      { title: "Report stampabili", desc: "Confronti prima/dopo esportati in un PDF pulito e pronto per la stampa per clienti o archivi." },
      { title: "11 lingue + RTL", desc: "Completamente localizzato, incluso l'arabo da destra a sinistra — pronto per qualsiasi officina, ovunque." },
    ],
  },
  showcase: {
    label: "Guardala in azione", heading: "Schermate reali dall'app Thortek.",
    items: [
      { tag: "Quick Align", title: "Telemetria live a quattro ruote", desc: "Guarda campanatura e convergenza di ogni angolo aggiornarsi in tempo reale, valutate rispetto alla specifica di fabbrica, con gomme che si inclinano muovendo il telefono." },
      { tag: "Inclinometro", title: "Un inclinometro digitale di precisione", desc: "Un quadrante radiale trasforma i sensori di movimento del telefono in una lettura di campanatura e incidenza di livello professionale — a una frazione di grado." },
      { tag: "Scansione visiva", title: "L'IA legge l'angolo per te", desc: "Punta la fotocamera, scorri l'asse e il motore di visione IA rileva campanatura e convergenza dall'immagine con un indicatore di affidabilità live." },
      { tag: "Garage", title: "Ogni specifica, decodificata e pronta", desc: "Decodifica qualsiasi VIN, scegli tra oltre 150 profili di fabbrica, applica preset e conserva veicoli personalizzati — tutto in un posto." },
    ],
  },
  how: {
    label: "Come funziona", heading: "Dal cortile alla specifica in tre passi.", inSpec: "In specifica",
    steps: [
      { title: "Scegli il veicolo", desc: "Decodifica un VIN o scegli tra oltre 150 profili di fabbrica. Aggiungi preset di sollevamento all'istante." },
      { title: "Misura", desc: "Usa l'inclinometro, il cruscotto a sensori g o la scansione IA per catturare campanatura, incidenza e convergenza." },
      { title: "Regola e registra", desc: "Confronta con la specifica OEM, valuta ogni ruota ed esporta un report pronto per la stampa." },
    ],
  },
  compare: {
    label: "Perché Thortek", heading: "Thortek vs. strumenti tradizionali.",
    intro: "Gli strumenti sono o un ponte costoso o un'app monouso. Thortek è l'intero reparto — senza hardware, con IA e pagato una sola volta.",
    thortek: "Thortek", typical: "Strumenti tradizionali",
    included: "Incluso", partial: "A volte / costo extra", notAvail: "Non disponibile",
    cta: "Accesso a vita",
    rows: [
      { f: "Pagamento unico — nessun abbonamento", note: "Paghi una volta, tuo per sempre" },
      { f: "Funziona solo col telefono — senza hardware" },
      { f: "La fotocamera IA legge campanatura e convergenza" },
      { f: "Sovrapposizione angolo live (AR)" },
      { f: "Diagnosi usura pneumatici con IA" },
      { f: "Calcolatore bilanciamento angoli" },
      { f: "Oltre 150 specifiche + decodifica VIN" },
      { f: "Report prima/dopo condivisibili" },
      { f: "11 lingue + supporto RTL" },
      { f: "Si installa su qualsiasi telefono o tablet" },
      { f: "Nessun login — dati privati e locali" },
    ],
  },
  pricing: {
    label: "Prezzo semplice", heading: "Un pagamento. Tuo per sempre.",
    subtitle: "Niente abbonamenti né costi per veicolo. Paghi una volta e possiedi l'intera suite.",
    cardTitle: "Licenza a vita", cardSub: "Suite completa · Tutte le funzioni", bestValue: "Miglior valore",
    unit: "USD · una tantum", buyNow: "Acquista ora",
    secure: "Checkout sicuro Stripe", worldwide: "Carte di tutto il mondo",
    intlNote: "Clienti internazionali benvenuti — paga nella valuta della tua carta dal Brasile e da oltre 190 paesi.",
    includes: [
      "Cruscotto di telemetria live Quick Align",
      "Inclinometro: campanatura + sterzata incidenza guidata",
      "Scansione visiva IA con panoramica dell'asse",
      "Garage multi-veicolo + decodifica VIN",
      "Preset di sollevamento e veicoli personalizzati",
      "Report di sessione in PDF stampabili",
      "Tutte le 11 lingue, aggiornamenti a vita",
    ],
  },
  testimonials: {
    label: "Affidabile ai box", heading: "Creato per appassionati e professionisti.",
    quotes: [
      { q: "Ho regolato la campanatura da pista nel paddock senza portarla in officina. La sterzata incidenza è incredibilmente precisa.", r: "Pilota time-attack" },
      { q: "La scansione IA legge la convergenza più in fretta del mio setup con i fili. Si ripaga già il primo weekend.", r: "Meccanico indipendente" },
      { q: "150 specifiche di fabbrica e decodifica VIN in un posto — tutto il team lo usa a ogni lavoro di bilanciamento.", r: "Titolare officina sospensioni" },
    ],
  },
  faq: {
    label: "Domande", heading: "Tutto ciò che devi sapere.",
    items: [
      { q: "È davvero un acquisto unico?", a: "Sì. Paghi {{price}} una volta e l'intera suite Thortek Performance è tua — niente canoni mensili, niente costi per veicolo e aggiornamenti futuri inclusi." },
      { q: "Posso acquistare fuori dagli Stati Uniti?", a: "Assolutamente. Il checkout usa Stripe e accetta carte dal Brasile e da oltre 190 paesi. La tua banca converte nella tua valuta automaticamente." },
      { q: "Serve hardware speciale?", a: "Nessun ponte né sensore. Thortek usa i sensori di movimento e la fotocamera del telefono. Una superficie piana e livellata dà le letture più precise." },
      { q: "Come accedo all'app dopo l'acquisto?", a: "Appena il pagamento è confermato ricevi una chiave di licenza e puoi avviare l'app completa nel browser — senza download." },
      { q: "Quali veicoli sono supportati?", a: "Oltre 150 veicoli con specifica di fabbrica fino al 2026, più decodifica VIN e profili personalizzati, per allineare quasi tutto." },
      { q: "I miei dati sono privati?", a: "Le tue sessioni di assetto sono salvate localmente sul dispositivo. Nessun account o login richiesto." },
    ],
  },
  finalCta: { headingA: "Pronto a", headingB: "mettere a punto?", subtitle: "Ottieni la suite completa oggi con un unico pagamento di {{price}}.", buy: "Accesso a vita", launch: "Apri l'app" },
  footer: { features: "Funzioni", pricing: "Prezzo", disclaimer: "Avvertenze", terms: "Termini", privacy: "Privacy", refunds: "Rimborsi", contact: "Contatti", launchApp: "Apri app" },
};

const zh = {
  nav: { features: "功能", how: "工作原理", pricing: "价格", faq: "常见问题" },
  header: { launchApp: "打开应用", buy: "购买" },
  hero: {
    eyebrow: "专业四轮定位 · 装进口袋",
    title1: "店级定位，", title2: "无需定位架。",
    subtitle: "Thortek Performance 把你的手机变成精密倾角仪和 AI 视觉设备。测量外倾、主销后倾和前束，解码任意车架号（VIN），并调校 150+ 原厂规格——就在自家车道。",
    getLifetime: "获取终身访问", tryApp: "试用应用",
    oneTime: "一次性付款", noSub: "无订阅", buyAnywhere: "随地购买",
  },
  statLabels: ["原厂规格车型", "传感器分辨率", "语言", "视觉扫描引擎"],
  features: {
    label: "一款应用搞定一切", heading: "整个定位工位，为你的手机重新设计。",
    cards: [
      { title: "Quick Align 遥测", desc: "四轮实时仪表盘，g 传感器轮子随 OEM 目标实时倾斜。" },
      { title: "免手操作语音采集", desc: "只需说出“capture”即可锁定读数——在车底下无需触碰或够取手机。" },
      { title: "倾角仪", desc: "外倾数字倾角仪，并带引导式后倾转向扫描——精确到零点几度。" },
      { title: "AI 视觉扫描", desc: "对准相机，拍摄平滑的全景轴扫，让 AI 为你读取外倾和前束。" },
      { title: "多车车库", desc: "150+ 原厂规格（至 2026 年）、VIN 解码、升降预设与自定义车型档案。" },
      { title: "可打印记录", desc: "前后对比导出为干净、可打印的 PDF 报告，用于客户或存档。" },
      { title: "11 种语言 + RTL", desc: "完全本地化，含从右到左的阿拉伯语——随时适用于任何车间。" },
    ],
  },
  showcase: {
    label: "实际演示", heading: "来自 Thortek 应用的真实界面。",
    items: [
      { tag: "Quick Align", title: "四轮实时遥测", desc: "实时查看每个车角的外倾和前束更新，按原厂规格评定合格/不合格，轮胎随手机移动而倾斜。" },
      { tag: "倾角仪", title: "精密数字倾角仪", desc: "战术径向表盘把手机的运动传感器变成店级外倾与后倾读数——精确到零点几度。" },
      { tag: "视觉扫描", title: "AI 为你读取角度", desc: "对准相机、扫过车轴，AI 视觉引擎便从图像中检测外倾和前束，并带实时置信度表。" },
      { tag: "车库", title: "每项规格，已解码并就绪", desc: "解码任意 VIN，从 150+ 原厂档案中选择，应用升降预设并保存自定义车型——全部集于一处。" },
    ],
  },
  how: {
    label: "工作原理", heading: "三步，从车道到达标。", inSpec: "达标",
    steps: [
      { title: "选择车辆", desc: "解码 VIN 或从 150+ 原厂档案中选择。即时添加升降预设。" },
      { title: "测量", desc: "使用倾角仪、g 传感器仪表盘或 AI 相机扫描来采集外倾、后倾和前束。" },
      { title: "调校并记录", desc: "与 OEM 规格对比，为每个车轮评定合格/不合格，然后导出可打印的报告。" },
    ],
  },
  compare: {
    label: "为什么选 Thortek", heading: "Thortek 对比传统定位工具。",
    intro: "大多数工具要么是昂贵的定位架，要么是单一功能的应用。Thortek 是整个工位——无需硬件、AI 驱动、一次付费。",
    thortek: "Thortek", typical: "传统工具",
    included: "包含", partial: "有时 / 额外收费", notAvail: "不可用",
    cta: "获取终身访问",
    rows: [
      { f: "一次性付款——无订阅", note: "付费一次，永久拥有" },
      { f: "只需手机即可使用——无需硬件" },
      { f: "AI 相机读取外倾和前束" },
      { f: "AR 实时角度叠加" },
      { f: "AI 轮胎磨损诊断" },
      { f: "四角配重计算器" },
      { f: "150+ 原厂规格 + VIN 解码" },
      { f: "可分享的前后对比报告" },
      { f: "11 种语言 + RTL 支持" },
      { f: "可安装到任意手机或平板" },
      { f: "无需登录——数据本地私密" },
    ],
  },
  pricing: {
    label: "简单定价", heading: "一次付款。永久拥有。",
    subtitle: "无订阅、无按车收费。付费一次即拥有完整套件。",
    cardTitle: "终身许可", cardSub: "完整套件 · 全部功能", bestValue: "超值",
    unit: "美元 · 一次性", buyNow: "立即购买",
    secure: "安全的 Stripe 结账", worldwide: "全球银行卡",
    intlNote: "欢迎国际客户——可用你所在地的银行卡货币付款，支持巴西及 190+ 个国家。",
    includes: [
      "Quick Align 实时遥测仪表盘",
      "倾角仪：外倾 + 引导式后倾扫描",
      "AI 视觉扫描与全景轴扫",
      "多车车库 + VIN 解码",
      "升降预设、自定义车型",
      "可打印的 PDF 会话记录",
      "全部 11 种语言，终身更新",
    ],
  },
  testimonials: {
    label: "维修区信赖之选", heading: "为爱好者与专业人士而造。",
    quotes: [
      { q: "我在维修区就调好了赛道外倾，不用拖去店里。后倾扫描精准得惊人。", r: "计时赛车手" },
      { q: "AI 扫描读取前束比我的拉线法还快。第一个周末就回本。", r: "独立技师" },
      { q: "150 项原厂规格和 VIN 解码集于一处——我的整个团队每次做配重都在用。", r: "悬挂车间老板" },
    ],
  },
  faq: {
    label: "问题", heading: "你需要了解的一切。",
    items: [
      { q: "这真的是一次性购买吗？", a: "是的。支付 {{price}} 一次，整个 Thortek Performance 套件即归你所有——无月费、无按车收费，且包含未来更新。" },
      { q: "我可以在美国境外购买吗？", a: "当然可以。结账由 Stripe 提供，接受巴西及 190+ 个国家的银行卡。你的银行会自动换算为当地货币。" },
      { q: "我需要特殊硬件吗？", a: "无需定位架或传感器。Thortek 使用你手机的运动传感器和相机。平整水平的地面能获得最准确的读数。" },
      { q: "购买后如何使用应用？", a: "付款到账后，你会收到许可密钥，可直接在浏览器中启动完整应用——无需下载。" },
      { q: "支持哪些车型？", a: "150+ 款原厂规格车型（至 2026 年），外加 VIN 解码和自定义车型档案，几乎可定位任何车辆。" },
      { q: "我的数据私密吗？", a: "你的定位会话数据存储在本地设备上。使用该工具无需账户或登录。" },
    ],
  },
  finalCta: { headingA: "准备好", headingB: "调校到位了吗？", subtitle: "今天只需一次性支付 {{price}} 即可拥有完整套件。", buy: "购买终身访问", launch: "打开应用" },
  footer: { features: "功能", pricing: "价格", disclaimer: "免责声明", terms: "条款", privacy: "隐私", refunds: "退款", contact: "联系", launchApp: "打开应用" },
};

const ja = {
  nav: { features: "機能", how: "使い方", pricing: "料金", faq: "よくある質問" },
  header: { launchApp: "アプリを開く", buy: "購入" },
  hero: {
    eyebrow: "プロ仕様のホイールアライメント · ポケットに",
    title1: "ショップ級アライメント、", title2: "アライメントラック不要。",
    subtitle: "Thortek Performance はスマホを高精度傾斜計と AI ビジョン機器に変えます。キャンバー・キャスター・トーを測定し、あらゆる VIN を解読、150 以上の純正スペックを自宅の駐車スペースで調整できます。",
    getLifetime: "生涯アクセスを取得", tryApp: "アプリを試す",
    oneTime: "一度きりの支払い", noSub: "サブスクなし", buyAnywhere: "どこからでも購入",
  },
  statLabels: ["純正スペック車種", "センサー分解能", "言語", "ビジュアルスキャンエンジン"],
  features: {
    label: "すべてが1つのアプリに", heading: "アライメント作業場まるごと、スマホ向けに再設計。",
    cards: [
      { title: "Quick Align テレメトリー", desc: "4輪ライブダッシュボード。gセンサーのホイールが OEM 目標値に対しリアルタイムで傾きます。" },
      { title: "ハンズフリー音声キャプチャ", desc: "「capture」と言うだけで測定値をロック——車体の下でスマホに触れたり手を伸ばしたりする必要はありません。" },
      { title: "傾斜計", desc: "キャンバー用のデジタル傾斜計に加え、ガイド付きキャスター操舵スイープ——1度未満の精度。" },
      { title: "AI ビジュアルスキャン", desc: "カメラを向け、滑らかなパノラマ軸スイープを撮影。AI がキャンバーとトーを読み取ります。" },
      { title: "マルチ車両ガレージ", desc: "2026年までの150以上の純正スペック、VIN 解読、リフト/レベリングのプリセット、カスタム車両プロファイル。" },
      { title: "印刷可能な記録", desc: "ビフォー/アフター比較を、顧客用や記録用のきれいな印刷対応 PDF レポートに出力。" },
      { title: "11言語 + RTL", desc: "右から左のアラビア語を含め完全ローカライズ——どんな工場でもすぐ使えます。" },
    ],
  },
  showcase: {
    label: "実際の動作", heading: "Thortek アプリの実画面。",
    items: [
      { tag: "Quick Align", title: "4輪ライブテレメトリー", desc: "各コーナーのキャンバーとトーがリアルタイムで更新され、純正スペックに対して合否判定。スマホを動かすとタイヤが傾きます。" },
      { tag: "傾斜計", title: "高精度デジタル傾斜計", desc: "戦術的なラジアルゲージがスマホのモーションセンサーをショップ級のキャンバー・キャスター表示に——1度未満の精度。" },
      { tag: "ビジュアルスキャン", title: "AI が角度を読み取る", desc: "カメラを向けて軸をスイープすると、AI ビジョンエンジンが画像からキャンバーとトーを検出し、信頼度をライブ表示。" },
      { tag: "ガレージ", title: "あらゆるスペックを解読・準備済み", desc: "任意の VIN を解読し、150以上の純正プロファイルから選択、プリセットを適用、カスタム車両も保存——すべて一か所で。" },
    ],
  },
  how: {
    label: "使い方", heading: "駐車スペースから基準内まで3ステップ。", inSpec: "基準内",
    steps: [
      { title: "車両を選ぶ", desc: "VIN を解読するか、150以上の純正プロファイルから選択。リフトプリセットも即追加。" },
      { title: "測定する", desc: "傾斜計、gセンサーダッシュボード、または AI カメラスキャンでキャンバー・キャスター・トーを取得。" },
      { title: "調整して記録", desc: "OEM スペックと比較し、各ホイールを合否判定、印刷対応レポートを出力。" },
    ],
  },
  compare: {
    label: "Thortek を選ぶ理由", heading: "Thortek と一般的なツールの比較。",
    intro: "多くのツールは高価なラックか、単機能アプリのいずれか。Thortek は作業場まるごと——ハードウェア不要、AI 搭載、支払いは一度きり。",
    thortek: "Thortek", typical: "一般的なツール",
    included: "含む", partial: "場合により / 追加費用", notAvail: "利用不可",
    cta: "生涯アクセスを取得",
    rows: [
      { f: "一度きりの支払い——サブスクなし", note: "一度払えば、ずっとあなたのもの" },
      { f: "スマホだけで動作——ハードウェア不要" },
      { f: "AI カメラがキャンバーとトーを読み取り" },
      { f: "AR ライブ角度オーバーレイ" },
      { f: "AI タイヤ摩耗診断" },
      { f: "コーナーバランス計算機" },
      { f: "150以上の純正スペック + VIN 解読" },
      { f: "共有可能なビフォー/アフターレポート" },
      { f: "11言語 + RTL 対応" },
      { f: "あらゆるスマホ・タブレットにインストール" },
      { f: "ログイン不要——データはローカルで非公開" },
    ],
  },
  pricing: {
    label: "シンプルな料金", heading: "一度の支払い。ずっとあなたのもの。",
    subtitle: "サブスクなし、車両ごとの料金なし。一度払えば全機能があなたのものに。",
    cardTitle: "生涯ライセンス", cardSub: "全機能 · フルスイート", bestValue: "最もお得",
    unit: "USD · 一度きり", buyNow: "今すぐ購入",
    secure: "安全な Stripe 決済", worldwide: "世界中のカード対応",
    intlNote: "海外のお客様も歓迎——ブラジルや190以上の国から、お使いのカードの通貨でお支払いいただけます。",
    includes: [
      "Quick Align ライブテレメトリーダッシュボード",
      "傾斜計：キャンバー + ガイド付きキャスタースイープ",
      "AI ビジュアルスキャン（パノラマ軸スイープ）",
      "マルチ車両ガレージ + VIN 解読",
      "リフト/レベリングプリセット、カスタム車両",
      "印刷可能な PDF セッション記録",
      "全11言語、生涯アップデート",
    ],
  },
  testimonials: {
    label: "ピットでの信頼", heading: "愛好家にもプロにも。",
    quotes: [
      { q: "ショップに運ばず、パドックでトラック用キャンバーを調整できた。キャスタースイープは驚くほど正確。", r: "タイムアタックドライバー" },
      { q: "AI スキャンは糸出しよりトーを速く読む。初週末で元が取れる。", r: "独立系メカニック" },
      { q: "150の純正スペックと VIN 解読が一か所に——うちのチームはコーナーバランスのたびに使っている。", r: "サスペンションショップ経営者" },
    ],
  },
  faq: {
    label: "質問", heading: "知っておくべきことすべて。",
    items: [
      { q: "本当に一度きりの購入ですか？", a: "はい。{{price}} を一度お支払いいただければ、Thortek Performance のフルスイートはあなたのもの——月額費用も車両ごとの課金もなく、今後のアップデートも含まれます。" },
      { q: "アメリカ国外からでも購入できますか？", a: "もちろんです。決済は Stripe で、ブラジルや190以上の国のカードに対応。銀行が自動的に現地通貨に換算します。" },
      { q: "特別なハードウェアは必要ですか？", a: "ラックやセンサーは不要です。Thortek はスマホのモーションセンサーとカメラを使用します。平らで水平な面が最も正確な測定を実現します。" },
      { q: "購入後、どうアプリにアクセスしますか？", a: "決済が完了した瞬間にライセンスキーが届き、ブラウザでフルアプリを起動できます——ダウンロード不要です。" },
      { q: "対応車種は？", a: "2026年までの150以上の純正スペック車種に加え、VIN 解読とカスタム車両プロファイルで、ほぼあらゆる車を調整できます。" },
      { q: "データは非公開ですか？", a: "アライメントのセッションはお使いのデバイスにローカル保存されます。ツールの利用にアカウントやログインは不要です。" },
    ],
  },
  finalCta: { headingA: "調整の", headingB: "準備はいい？", subtitle: "今日、一度きりの {{price}} でフルスイートを手に入れましょう。", buy: "生涯アクセスを購入", launch: "アプリを開く" },
  footer: { features: "機能", pricing: "料金", disclaimer: "免責事項", terms: "利用規約", privacy: "プライバシー", refunds: "返金", contact: "お問い合わせ", launchApp: "アプリを開く" },
};

const ar = {
  nav: { features: "الميزات", how: "كيف يعمل", pricing: "السعر", faq: "الأسئلة الشائعة" },
  header: { launchApp: "افتح التطبيق", buy: "اشترِ" },
  hero: {
    eyebrow: "ضبط زوايا احترافي · في جيبك",
    title1: "ضبط بمستوى الورشة،", title2: "دون جهاز ضبط.",
    subtitle: "يحوّل Thortek Performance هاتفك إلى مقياس ميل دقيق ونظام رؤية بالذكاء الاصطناعي. قِس زاوية الميل والكاستر والتوجيه، وفكّ شفرة أي رقم هيكل (VIN)، واضبط أكثر من 150 مواصفة مصنع — في ممر منزلك.",
    getLifetime: "احصل على وصول مدى الحياة", tryApp: "جرّب التطبيق",
    oneTime: "دفعة واحدة", noSub: "دون اشتراك", buyAnywhere: "اشترِ من أي مكان",
  },
  statLabels: ["مركبات بمواصفات المصنع", "دقة المستشعر", "اللغات", "محرك المسح البصري"],
  features: {
    label: "كل شيء في تطبيق واحد", heading: "حظيرة الضبط كاملةً، أُعيد تصميمها لهاتفك.",
    cards: [
      { title: "قياس Quick Align", desc: "لوحة حية لأربع عجلات مع مستشعرات تتمايل آنيًا مقابل أهداف المصنع (OEM)." },
      { title: "التقاط صوتي بلا استخدام اليدين", desc: "قل فقط «capture» لتثبيت القراءة — دون لمس الهاتف أو الوصول إليه وأنت أسفل المركبة." },
      { title: "مقياس الميل", desc: "مقياس ميل رقمي لزاوية الميل مع مسح موجّه للكاستر — بدقة جزء من الدرجة." },
      { title: "مسح بصري بالذكاء الاصطناعي", desc: "وجّه الكاميرا، والتقط مسحًا بانوراميًا سلسًا للمحور، ودع الذكاء الاصطناعي يقرأ الميل والتوجيه." },
      { title: "كراج متعدد المركبات", desc: "أكثر من 150 مواصفة مصنع حتى 2026، وفك شفرة VIN، وإعدادات رفع مسبقة وملفات مركبات مخصّصة." },
      { title: "سجلات قابلة للطباعة", desc: "مقارنات قبل/بعد تُصدَّر إلى تقرير PDF نظيف جاهز للطباعة للعملاء أو السجلات." },
      { title: "11 لغة + دعم RTL", desc: "مترجَم بالكامل بما في ذلك العربية من اليمين إلى اليسار — جاهز لأي ورشة في أي مكان." },
    ],
  },
  showcase: {
    label: "شاهده أثناء العمل", heading: "شاشات حقيقية من تطبيق Thortek.",
    items: [
      { tag: "Quick Align", title: "قياس حي لأربع عجلات", desc: "شاهد ميل وتوجيه كل زاوية يتحدّثان آنيًا، بتقييم ناجح/راسب مقابل مواصفة المصنع، مع إطارات تميل عند تحريك الهاتف." },
      { tag: "مقياس الميل", title: "مقياس ميل رقمي دقيق", desc: "مؤشر شعاعي يحوّل مستشعرات هاتفك إلى قراءة ميل وكاستر بمستوى الورشة — بدقة جزء من الدرجة." },
      { tag: "المسح البصري", title: "الذكاء الاصطناعي يقرأ الزاوية بدلًا عنك", desc: "وجّه الكاميرا وامسح المحور، فيكتشف محرك الرؤية الميل والتوجيه من الصورة مع مؤشر ثقة حي." },
      { tag: "الكراج", title: "كل مواصفة، مفكوكة الشفرة وجاهزة", desc: "فكّ شفرة أي VIN، واختر من أكثر من 150 ملف مصنع، وطبّق الإعدادات المسبقة، واحفظ مركبات مخصّصة — كل ذلك في مكان واحد." },
    ],
  },
  how: {
    label: "كيف يعمل", heading: "من الممر إلى ضمن المواصفة في ثلاث خطوات.", inSpec: "ضمن المواصفة",
    steps: [
      { title: "اختر مركبتك", desc: "فكّ شفرة VIN أو اختر من أكثر من 150 ملف مصنع. أضف إعدادات الرفع فورًا." },
      { title: "قِس", desc: "استخدم مقياس الميل أو لوحة المستشعرات أو المسح بالذكاء الاصطناعي لالتقاط الميل والكاستر والتوجيه." },
      { title: "اضبط وسجّل", desc: "قارن بمواصفة المصنع، وقيّم كل عجلة، ثم صدّر تقريرًا جاهزًا للطباعة." },
    ],
  },
  compare: {
    label: "لماذا Thortek", heading: "Thortek مقابل الأدوات التقليدية.",
    intro: "معظم الأدوات إمّا جهاز ضبط باهظ أو تطبيق بوظيفة واحدة. أمّا Thortek فهو الحظيرة كاملةً — دون أجهزة، مدعوم بالذكاء الاصطناعي، وبدفعة واحدة.",
    thortek: "Thortek", typical: "الأدوات التقليدية",
    included: "مُضمَّن", partial: "أحيانًا / تكلفة إضافية", notAvail: "غير متوفر",
    cta: "احصل على وصول مدى الحياة",
    rows: [
      { f: "دفعة واحدة — دون اشتراك", note: "ادفع مرة، وهو لك للأبد" },
      { f: "يعمل بهاتفك فقط — دون أجهزة" },
      { f: "كاميرا الذكاء الاصطناعي تقرأ الميل والتوجيه" },
      { f: "تراكب زاوية حي بالواقع المعزّز (AR)" },
      { f: "تشخيص تآكل الإطارات بالذكاء الاصطناعي" },
      { f: "حاسبة موازنة الزوايا" },
      { f: "أكثر من 150 مواصفة + فك شفرة VIN" },
      { f: "تقارير قبل/بعد قابلة للمشاركة" },
      { f: "11 لغة + دعم RTL" },
      { f: "يُثبَّت على أي هاتف أو جهاز لوحي" },
      { f: "دون تسجيل دخول — بيانات خاصة ومحلية" },
    ],
  },
  pricing: {
    label: "تسعير بسيط", heading: "دفعة واحدة. لك للأبد.",
    subtitle: "دون اشتراكات ولا رسوم لكل مركبة. ادفع مرة واحدة وتملّك الحزمة الكاملة.",
    cardTitle: "ترخيص مدى الحياة", cardSub: "الحزمة الكاملة · كل الميزات", bestValue: "أفضل قيمة",
    unit: "دولار · مرة واحدة", buyNow: "اشترِ الآن",
    secure: "دفع آمن عبر Stripe", worldwide: "بطاقات من كل العالم",
    intlNote: "العملاء الدوليون مرحّب بهم — ادفع بعملة بطاقتك من البرازيل وأكثر من 190 دولة.",
    includes: [
      "لوحة قياس حية Quick Align",
      "مقياس الميل: الميل + مسح كاستر موجّه",
      "مسح بصري بالذكاء الاصطناعي مع مسح بانورامي للمحور",
      "كراج متعدد المركبات + فك شفرة VIN",
      "إعدادات رفع مسبقة ومركبات مخصّصة",
      "سجلات جلسات PDF قابلة للطباعة",
      "كل اللغات الـ11، تحديثات مدى الحياة",
    ],
  },
  testimonials: {
    label: "موثوق في الحلبة", heading: "مصمَّم للهواة والمحترفين معًا.",
    quotes: [
      { q: "ضبطت ميل حلبتي في منطقة الصيانة دون نقلها إلى ورشة. مسح الكاستر دقيق بشكل مذهل.", r: "سائق تايم أتاك" },
      { q: "المسح بالذكاء الاصطناعي يقرأ التوجيه أسرع من إعداد الخيوط لديّ. يسترد ثمنه في أول عطلة أسبوع.", r: "ميكانيكي مستقل" },
      { q: "150 مواصفة مصنع وفك شفرة VIN في مكان واحد — فريقي كله يستخدمه في كل عملية موازنة.", r: "صاحب ورشة تعليق" },
    ],
  },
  faq: {
    label: "أسئلة", heading: "كل ما تحتاج معرفته.",
    items: [
      { q: "هل هو حقًّا شراء لمرة واحدة؟", a: "نعم. ادفع {{price}} مرة واحدة وتصبح حزمة Thortek Performance الكاملة ملكك — دون رسوم شهرية ولا رسوم لكل مركبة، مع تضمين التحديثات المستقبلية." },
      { q: "هل يمكنني الشراء من خارج الولايات المتحدة؟", a: "بالتأكيد. الدفع عبر Stripe ويقبل بطاقات من البرازيل وأكثر من 190 دولة. يحوّل بنكك تلقائيًا إلى عملتك المحلية." },
      { q: "هل أحتاج إلى أجهزة خاصة؟", a: "لا حاجة لجهاز ضبط أو مستشعرات. يستخدم Thortek مستشعرات الحركة والكاميرا في هاتفك. سطح مستوٍ أفقي يمنح أدق القراءات." },
      { q: "كيف أصل إلى التطبيق بعد الشراء؟", a: "بمجرد تأكيد الدفع ستحصل على مفتاح ترخيص ويمكنك تشغيل التطبيق الكامل في متصفحك — دون تنزيل." },
      { q: "ما المركبات المدعومة؟", a: "أكثر من 150 مركبة بمواصفات المصنع حتى 2026 بالإضافة إلى فك شفرة VIN وملفات مركبات مخصّصة، لتضبط أي شيء تقريبًا." },
      { q: "هل بياناتي خاصة؟", a: "تُحفظ جلسات الضبط محليًا على جهازك. لا حاجة لحساب أو تسجيل دخول لاستخدام الأداة." },
    ],
  },
  finalCta: { headingA: "جاهز", headingB: "للضبط؟", subtitle: "امتلك الحزمة الكاملة اليوم بدفعة واحدة قدرها {{price}}.", buy: "اشترِ وصول مدى الحياة", launch: "افتح التطبيق" },
  footer: { features: "الميزات", pricing: "السعر", disclaimer: "إخلاء مسؤولية", terms: "الشروط", privacy: "الخصوصية", refunds: "المبالغ المستردة", contact: "اتصل بنا", launchApp: "افتح التطبيق" },
};

const hi = {
  nav: { features: "विशेषताएँ", how: "यह कैसे काम करता है", pricing: "कीमत", faq: "सामान्य प्रश्न" },
  header: { launchApp: "ऐप खोलें", buy: "खरीदें" },
  hero: {
    eyebrow: "प्रो व्हील अलाइनमेंट · आपकी जेब में",
    title1: "वर्कशॉप-स्तरीय अलाइनमेंट,", title2: "बिना अलाइनमेंट रैक के.",
    subtitle: "Thortek Performance आपके फ़ोन को एक सटीक इनक्लिनोमीटर और AI विज़न रिग बना देता है। कैम्बर, कास्टर और टो मापें, कोई भी VIN डिकोड करें, और 150+ फ़ैक्टरी स्पेक्स को अपने ड्राइववे में ही सेट करें।",
    getLifetime: "लाइफ़टाइम एक्सेस पाएँ", tryApp: "ऐप आज़माएँ",
    oneTime: "एकमुश्त भुगतान", noSub: "कोई सब्सक्रिप्शन नहीं", buyAnywhere: "कहीं से भी खरीदें",
  },
  statLabels: ["फ़ैक्टरी-स्पेक वाहन", "सेंसर रिज़ॉल्यूशन", "भाषाएँ", "विज़ुअल स्कैन इंजन"],
  features: {
    label: "सब कुछ एक ऐप में", heading: "पूरा अलाइनमेंट बे, आपके फ़ोन के लिए फिर से तैयार।",
    cards: [
      { title: "Quick Align टेलीमेट्री", desc: "चार पहियों का लाइव डैशबोर्ड, जहाँ g-सेंसर वाले पहिये OEM लक्ष्यों के सामने रियल-टाइम में झुकते हैं।" },
      { title: "हैंड्स-फ़्री वॉइस कैप्चर", desc: "रीडिंग लॉक करने के लिए बस “capture” कहें — वाहन के नीचे रहते हुए फ़ोन छूने या उस तक पहुँचने की ज़रूरत नहीं।" },
      { title: "टिल्ट गेज", desc: "कैम्बर के लिए डिजिटल इनक्लिनोमीटर और गाइडेड कास्टर स्टीयरिंग-स्वीप — डिग्री के अंश तक सटीक।" },
      { title: "AI विज़ुअल स्कैन", desc: "कैमरा तानें, एक सहज पैनोरमिक एक्सल स्वीप कैप्चर करें, और AI को कैम्बर व टो पढ़ने दें।" },
      { title: "मल्टी-व्हीकल गैराज", desc: "2026 तक 150+ फ़ैक्टरी स्पेक्स, VIN डिकोडिंग, लिफ़्ट/लेवलिंग प्रीसेट और कस्टम वाहन प्रोफ़ाइल।" },
      { title: "प्रिंट-योग्य रिकॉर्ड", desc: "पहले/बाद की तुलनाएँ ग्राहकों या रिकॉर्ड के लिए साफ़, प्रिंट-तैयार PDF रिपोर्ट में एक्सपोर्ट करें।" },
      { title: "11 भाषाएँ + RTL", desc: "पूरी तरह लोकलाइज़्ड, दाएँ-से-बाएँ अरबी सहित — किसी भी गैराज के लिए, कहीं भी तैयार।" },
    ],
  },
  showcase: {
    label: "इसे क्रिया में देखें", heading: "Thortek ऐप की असली स्क्रीन।",
    items: [
      { tag: "Quick Align", title: "चार पहियों की लाइव टेलीमेट्री", desc: "हर कोने का कैम्बर और टो रियल-टाइम में अपडेट होते देखें, फ़ैक्टरी स्पेक के सामने पास/फ़ेल, और फ़ोन हिलाने पर झुकते टायरों के साथ।" },
      { tag: "टिल्ट गेज", title: "एक सटीक डिजिटल इनक्लिनोमीटर", desc: "एक रेडियल गेज आपके फ़ोन के मोशन सेंसर को वर्कशॉप-स्तरीय कैम्बर व कास्टर रीडआउट में बदल देता है — डिग्री के अंश तक सटीक।" },
      { tag: "विज़ुअल स्कैन", title: "AI आपके लिए कोण पढ़ता है", desc: "कैमरा तानें, एक्सल स्वीप करें, और AI विज़न इंजन छवि से कैम्बर व टो का पता लगाता है, साथ में लाइव कॉन्फ़िडेंस मीटर।" },
      { tag: "गैराज", title: "हर स्पेक, डिकोडेड और तैयार", desc: "कोई भी VIN डिकोड करें, 150+ फ़ैक्टरी प्रोफ़ाइल में से चुनें, प्रीसेट लागू करें और कस्टम वाहन रखें — सब एक जगह।" },
    ],
  },
  how: {
    label: "यह कैसे काम करता है", heading: "ड्राइववे से स्पेक-के-भीतर तक, तीन चरणों में।", inSpec: "स्पेक के भीतर",
    steps: [
      { title: "अपना वाहन चुनें", desc: "VIN डिकोड करें या 150+ फ़ैक्टरी प्रोफ़ाइल में से चुनें। लिफ़्ट प्रीसेट तुरंत जोड़ें।" },
      { title: "मापें", desc: "कैम्बर, कास्टर और टो कैप्चर करने के लिए टिल्ट गेज, g-सेंसर डैशबोर्ड या AI कैमरा स्कैन का उपयोग करें।" },
      { title: "सेट करें और लॉग करें", desc: "OEM स्पेक से तुलना करें, हर पहिये को पास/फ़ेल दें, फिर प्रिंट-तैयार रिपोर्ट एक्सपोर्ट करें।" },
    ],
  },
  compare: {
    label: "Thortek क्यों", heading: "Thortek बनाम सामान्य टूल।",
    intro: "अधिकांश टूल या तो महँगा रैक हैं या एक-काम वाला ऐप। Thortek पूरा बे है — बिना हार्डवेयर, AI-संचालित, और एक बार का भुगतान।",
    thortek: "Thortek", typical: "सामान्य टूल",
    included: "शामिल", partial: "कभी-कभी / अतिरिक्त शुल्क", notAvail: "उपलब्ध नहीं",
    cta: "लाइफ़टाइम एक्सेस पाएँ",
    rows: [
      { f: "एकमुश्त कीमत — कोई सब्सक्रिप्शन नहीं", note: "एक बार भुगतान करें, हमेशा के लिए आपका" },
      { f: "सिर्फ़ आपके फ़ोन से काम करता है — कोई हार्डवेयर नहीं" },
      { f: "AI कैमरा कैम्बर और टो पढ़ता है" },
      { f: "AR लाइव कोण ओवरले" },
      { f: "AI टायर-घिसाव निदान" },
      { f: "कॉर्नर-बैलेंस कैलकुलेटर" },
      { f: "150+ फ़ैक्टरी स्पेक्स + VIN डिकोड" },
      { f: "शेयर करने योग्य पहले/बाद की रिपोर्ट" },
      { f: "11 भाषाएँ + RTL समर्थन" },
      { f: "किसी भी फ़ोन या टैबलेट पर इंस्टॉल" },
      { f: "कोई लॉगिन नहीं — निजी, स्थानीय डेटा" },
    ],
  },
  pricing: {
    label: "सरल कीमत", heading: "एक भुगतान। हमेशा के लिए आपका।",
    subtitle: "कोई सब्सक्रिप्शन नहीं, प्रति-वाहन शुल्क नहीं। एक बार भुगतान करें और पूरा सूट पाएँ।",
    cardTitle: "लाइफ़टाइम लाइसेंस", cardSub: "पूरा सूट · सभी विशेषताएँ", bestValue: "सर्वोत्तम मूल्य",
    unit: "USD · एकमुश्त", buyNow: "अभी खरीदें",
    secure: "सुरक्षित Stripe चेकआउट", worldwide: "दुनिया भर के कार्ड",
    intlNote: "अंतरराष्ट्रीय ग्राहकों का स्वागत है — ब्राज़ील और 190+ देशों से अपने कार्ड की मुद्रा में भुगतान करें।",
    includes: [
      "Quick Align लाइव टेलीमेट्री डैशबोर्ड",
      "टिल्ट गेज: कैम्बर + गाइडेड कास्टर स्वीप",
      "पैनोरमिक एक्सल स्वीप के साथ AI विज़ुअल स्कैन",
      "मल्टी-व्हीकल गैराज + VIN डिकोडिंग",
      "लिफ़्ट/लेवलिंग प्रीसेट, कस्टम वाहन",
      "प्रिंट-योग्य PDF सत्र रिकॉर्ड",
      "सभी 11 भाषाएँ, लाइफ़टाइम अपडेट",
    ],
  },
  testimonials: {
    label: "पिट्स में भरोसेमंद", heading: "शौकीनों और पेशेवरों, दोनों के लिए बना।",
    quotes: [
      { q: "मैंने अपनी ट्रैक कैम्बर पैडॉक में ही सेट कर ली, शॉप ले जाए बिना। कास्टर स्वीप हैरतअंगेज़ रूप से सटीक है।", r: "टाइम-अटैक ड्राइवर" },
      { q: "AI स्कैन मेरे स्ट्रिंग सेटअप से तेज़ टो पढ़ता है। पहले ही वीकेंड में पैसे वसूल।", r: "स्वतंत्र मैकेनिक" },
      { q: "150 फ़ैक्टरी स्पेक्स और VIN डिकोड एक ही जगह — मेरी पूरी टीम हर कॉर्नर-बैलेंस काम में इसका उपयोग करती है।", r: "सस्पेंशन शॉप मालिक" },
    ],
  },
  faq: {
    label: "प्रश्न", heading: "वह सब जो आपको जानना चाहिए।",
    items: [
      { q: "क्या यह सचमुच एकमुश्त खरीद है?", a: "हाँ। {{price}} एक बार भुगतान करें और पूरा Thortek Performance सूट आपका — कोई मासिक शुल्क नहीं, प्रति-वाहन शुल्क नहीं, और भविष्य के अपडेट शामिल।" },
      { q: "क्या मैं अमेरिका के बाहर से खरीद सकता हूँ?", a: "बिल्कुल। चेकआउट Stripe द्वारा संचालित है और ब्राज़ील व 190+ देशों के कार्ड स्वीकार करता है। आपका बैंक अपने-आप आपकी मुद्रा में बदल देता है।" },
      { q: "क्या मुझे विशेष हार्डवेयर चाहिए?", a: "कोई रैक या सेंसर ज़रूरी नहीं। Thortek आपके फ़ोन के मोशन सेंसर और कैमरे का उपयोग करता है। समतल, सीधी सतह सबसे सटीक रीडिंग देती है।" },
      { q: "खरीद के बाद ऐप कैसे एक्सेस करूँ?", a: "भुगतान क्लियर होते ही आपको एक लाइसेंस की मिलेगी और आप पूरा ऐप सीधे अपने ब्राउज़र में खोल सकते हैं — कोई डाउनलोड नहीं।" },
      { q: "कौन-से वाहन समर्थित हैं?", a: "2026 तक 150+ फ़ैक्टरी-स्पेक वाहन, साथ ही VIN डिकोडिंग और कस्टम वाहन प्रोफ़ाइल, ताकि आप लगभग कुछ भी अलाइन कर सकें।" },
      { q: "क्या मेरा डेटा निजी है?", a: "आपके अलाइनमेंट सत्र आपके डिवाइस पर स्थानीय रूप से सहेजे जाते हैं। टूल उपयोग करने के लिए किसी खाते या लॉगिन की ज़रूरत नहीं।" },
    ],
  },
  finalCta: { headingA: "तैयार हैं", headingB: "सेट करने के लिए?", subtitle: "आज ही एकमुश्त {{price}} भुगतान में पूरा सूट पाएँ।", buy: "लाइफ़टाइम एक्सेस खरीदें", launch: "ऐप खोलें" },
  footer: { features: "विशेषताएँ", pricing: "कीमत", disclaimer: "अस्वीकरण", terms: "शर्तें", privacy: "गोपनीयता", refunds: "रिफ़ंड", contact: "संपर्क", launchApp: "ऐप खोलें" },
};

const ru = {
  nav: { features: "Функции", how: "Как это работает", pricing: "Цена", faq: "Вопросы" },
  header: { launchApp: "Открыть приложение", buy: "Купить" },
  hero: {
    eyebrow: "Профессиональный развал-схождение · В вашем кармане",
    title1: "Развал как в сервисе,", title2: "без стенда.",
    subtitle: "Thortek Performance превращает ваш телефон в точный инклинометр и систему ИИ-зрения. Измеряйте развал, кастер и схождение, декодируйте любой VIN и настраивайте более 150 заводских спецификаций — прямо на своей площадке.",
    getLifetime: "Пожизненный доступ", tryApp: "Попробовать приложение",
    oneTime: "Разовая оплата", noSub: "Без подписки", buyAnywhere: "Покупка из любой точки мира",
  },
  statLabels: ["Автомобилей с заводскими спеками", "Разрешение датчика", "Языков", "Движок визуального сканирования"],
  features: {
    label: "Всё в одном приложении", heading: "Весь пост развал-схождения, переосмысленный для телефона.",
    cards: [
      { title: "Телеметрия Quick Align", desc: "Живая панель для четырёх колёс с g-датчиками, которые наклоняются в реальном времени относительно заводских целей." },
      { title: "Голосовой захват без рук", desc: "Просто скажите «capture», чтобы зафиксировать показание — не нужно касаться телефона под автомобилем." },
      { title: "Инклинометр", desc: "Цифровой инклинометр для развала и управляемый прогон кастера — с точностью до доли градуса." },
      { title: "ИИ-визуальное сканирование", desc: "Наведите камеру, снимите плавную панораму оси, и ИИ считает развал и схождение за вас." },
      { title: "Гараж на много авто", desc: "Более 150 заводских спеков до 2026 года, декодирование VIN, пресеты лифта и профили авто." },
      { title: "Печатные протоколы", desc: "Сравнения «до/после» в виде чистого PDF-отчёта, готового к печати, для клиентов или архива." },
      { title: "11 языков + RTL", desc: "Полностью локализовано, включая арабский справа налево — готово для любого сервиса в любой точке мира." },
    ],
  },
  showcase: {
    label: "Посмотрите в действии", heading: "Реальные экраны приложения Thortek.",
    items: [
      { tag: "Quick Align", title: "Живая телеметрия четырёх колёс", desc: "Смотрите, как развал и схождение каждого угла обновляются в реальном времени, с оценкой «годен/не годен» по заводской спеке, а шины наклоняются при движении телефона." },
      { tag: "Инклинометр", title: "Точный цифровой инклинометр", desc: "Радиальная шкала превращает датчики движения телефона в сервисные показания развала и кастера — с точностью до доли градуса." },
      { tag: "Визуальное сканирование", title: "ИИ считывает угол за вас", desc: "Наведите камеру, пройдите по оси, и движок ИИ-зрения определит развал и схождение по изображению с живым индикатором уверенности." },
      { tag: "Гараж", title: "Каждый спек — декодирован и готов", desc: "Декодируйте любой VIN, выбирайте из 150+ заводских профилей, применяйте пресеты и храните свои авто — всё в одном месте." },
    ],
  },
  how: {
    label: "Как это работает", heading: "От площадки до нормы за три шага.", inSpec: "В норме",
    steps: [
      { title: "Выберите авто", desc: "Декодируйте VIN или выберите из 150+ заводских профилей. Мгновенно добавьте пресеты лифта." },
      { title: "Измерьте", desc: "Используйте инклинометр, панель g-датчиков или ИИ-сканирование камерой для захвата развала, кастера и схождения." },
      { title: "Настройте и запишите", desc: "Сравните с заводской спекой, оцените каждое колесо и экспортируйте отчёт, готовый к печати." },
    ],
  },
  compare: {
    label: "Почему Thortek", heading: "Thortek против обычных инструментов.",
    intro: "Большинство инструментов — это либо дорогой стенд, либо приложение с одной функцией. Thortek — это весь пост: без оборудования, на базе ИИ и с разовой оплатой.",
    thortek: "Thortek", typical: "Обычные инструменты",
    included: "Входит", partial: "Иногда / доплата", notAvail: "Недоступно",
    cta: "Пожизненный доступ",
    rows: [
      { f: "Разовая цена — без подписки", note: "Заплатите один раз — ваше навсегда" },
      { f: "Работает только с телефоном — без оборудования" },
      { f: "ИИ-камера считывает развал и схождение" },
      { f: "AR-наложение угла в реальном времени" },
      { f: "ИИ-диагностика износа шин" },
      { f: "Калькулятор поугловой развесовки" },
      { f: "150+ заводских спеков + декодирование VIN" },
      { f: "Отчёты «до/после» с возможностью поделиться" },
      { f: "11 языков + поддержка RTL" },
      { f: "Устанавливается на любой телефон или планшет" },
      { f: "Без входа — приватные локальные данные" },
    ],
  },
  pricing: {
    label: "Простая цена", heading: "Один платёж. Ваше навсегда.",
    subtitle: "Без подписок и платы за каждое авто. Заплатите один раз и владейте всем набором.",
    cardTitle: "Пожизненная лицензия", cardSub: "Весь набор · Все функции", bestValue: "Лучшая цена",
    unit: "USD · разово", buyNow: "Купить сейчас",
    secure: "Безопасная оплата Stripe", worldwide: "Карты со всего мира",
    intlNote: "Международные клиенты приветствуются — платите в валюте своей карты из Бразилии и 190+ стран.",
    includes: [
      "Живая панель телеметрии Quick Align",
      "Инклинометр: развал + управляемый прогон кастера",
      "ИИ-визуальное сканирование с панорамой оси",
      "Гараж на много авто + декодирование VIN",
      "Пресеты лифта, свои автомобили",
      "Печатные PDF-протоколы сессий",
      "Все 11 языков, пожизненные обновления",
    ],
  },
  testimonials: {
    label: "Проверено в боксах", heading: "Создано и для энтузиастов, и для профи.",
    quotes: [
      { q: "Я настроил трековый развал прямо в паддоке, не загоняя машину в сервис. Прогон кастера поразительно точен.", r: "Пилот тайм-атаки" },
      { q: "ИИ-сканирование считывает схождение быстрее моей верёвочной разметки. Окупается в первый же уикенд.", r: "Частный механик" },
      { q: "150 заводских спеков и декодирование VIN в одном месте — вся моя команда пользуется этим на каждой развесовке.", r: "Владелец цеха подвески" },
    ],
  },
  faq: {
    label: "Вопросы", heading: "Всё, что нужно знать.",
    items: [
      { q: "Это действительно разовая покупка?", a: "Да. Заплатите {{price}} один раз, и весь набор Thortek Performance ваш — без ежемесячной платы, без платы за авто, будущие обновления включены." },
      { q: "Можно ли купить за пределами США?", a: "Безусловно. Оплата через Stripe, принимаются карты из Бразилии и 190+ стран. Банк автоматически конвертирует в вашу валюту." },
      { q: "Нужно ли специальное оборудование?", a: "Стенд и датчики не нужны. Thortek использует датчики движения и камеру телефона. Ровная горизонтальная поверхность даёт самые точные показания." },
      { q: "Как получить доступ к приложению после покупки?", a: "Как только платёж пройдёт, вы получите лицензионный ключ и сможете запустить полное приложение прямо в браузере — без загрузки." },
      { q: "Какие автомобили поддерживаются?", a: "Более 150 авто с заводскими спеками до 2026 года, плюс декодирование VIN и свои профили, так что выставить можно почти всё." },
      { q: "Мои данные приватны?", a: "Сессии развал-схождения хранятся локально на вашем устройстве. Для работы с инструментом не нужны аккаунт или вход." },
    ],
  },
  finalCta: { headingA: "Готовы", headingB: "настроить?", subtitle: "Получите весь набор сегодня за разовый платёж {{price}}.", buy: "Купить пожизненный доступ", launch: "Открыть приложение" },
  footer: { features: "Функции", pricing: "Цена", disclaimer: "Отказ от ответственности", terms: "Условия", privacy: "Конфиденциальность", refunds: "Возвраты", contact: "Контакты", launchApp: "Открыть приложение" },
};

export const landingResources = { en, es, fr, de, pt, it, zh, ja, ar, hi, ru };
